import {Collection} from 'components/recommendations/leo-promotions-badge/types';
// eslint-disable-next-line import/no-cycle
import {BrandDetails} from 'components/recommendations/product-card/types';

import {ProductUnavailableReasonCodes} from './recommedation-types/recommendations';

export type MetafieldContract = {
  id: number;
  key: string;
  value: string;
};

export enum MarketplaceShopType {
  CONSIGNMENT = 'Consignment',
  DROPSHIP_SOD = 'Dropship_SOD',
  DROPSHIP_FBM = 'Dropship_FBM',
  MAKRO_FLEET = 'Makro_Fleet'
}

export enum ProductStatusType { //ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L32://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L32
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DRAFT = 'DRAFT'
}

export type Variant = {
  isExpressDelivery: boolean;
  id: string;
  sku: string;
  price: number;
  storeCode: string;
  outsideOfDeliveryZone: boolean;
  metafields?: MetafieldContract[];
  inventoryQuantity?: number;
  compareAtPrice: string;
  discountStartDate?: string;
  discountEndDate?: string;
  costPrice?: number;
  rmsStatus?: string;
  complexPromotion3P?: ComplexPromotions3P[];
};
export type BasketPromotion = {
  complexPromotionIds: number[];
  discountAmount: number;
  exvatCdi?: string;
  exvatSdi?: string;
  exvatUnitPrice?: string;
  exvatTotalPrice?: string;
  __typename: string;
};

export type ProDetails = {
  VAT_number: number;
  corporate_name: string;
  identification_number: string;
  tax_identification_number: string;
};

export type Shop = {
  shopId: number;
  shopName: string;
  marketplaceType: string | null;
  fulfilledByPlatform?: string;
  pro_details: ProDetails;
  shopLogo?: string;
};

export type ShippingInfo = {
  zone: string;
  shippingPrice?: number;
  deliveryTimeEarliestDays?: number;
  deliveryTimeLatestDays?: number;
  deliveryTimeEarliestDate?: Date;
  deliveryTimeLatestDate?: Date;
};

export type ProductMeasurement = {
  height?: number;
  length?: number;
  width?: number;
  dimensionUnit?: string;
  weight?: number;
  weightUnit?: string;
};

export type ProductImage = {
  mediaUrl?: string;
  mimeType?: string;
  type?: string;
};
export type SlabPriceTier = {
  tier: number;
  quantity: number;
  discount: number;
  priceInVat: number;
};

export type SlabPrices = {
  slabPriceTiers?: SlabPriceTier[];
  slabPriceDescription?: string;
};

export type Upcharge = {
  percentage?: number;
  minimumThreshold?: number;
};

export type UnavailableReason = {
  code: keyof typeof ProductUnavailableReasonCodes;
  message: string;
};

export declare class DeliveryDescription {
  singleLine?: string;
  summary?: string;
  popup?: string;
}

export type Product = {
  dateCreated: string | number | Date;
  normalisedRecommendationScore: string;
  deliveryDescription?: DeliveryDescription;
  isQuantityAdjusted: any;
  brandDetails?: BrandDetails;
  remainingQuantity: number;
  id: string;
  brand?: string;
  title: string;
  size?: string;
  status?: string;
  displayPrice: number;
  promotedPrice?: number;
  promotion?: BasketPromotion;
  quantitySetByUser?: number;
  originPrice: number;
  discountStartDate: string;
  discountEndDate: string;
  priceUnit: string;
  sku: string;
  skuCode?: string;
  description?: string;
  imageUrls: string[];
  seller: string;
  amountPerKg?: string;
  weightPerPiece?: string;
  originWeight?: number;
  finalWeight?: number;
  deliveryIn24Hrs?: boolean;
  sellerDetails: Shop;
  shippingInfo?: ShippingInfo[];
  variants: Variant[];
  totalInventory: number;
  boostedScore?: number;
  categoryIds?: string[];
  measurements: ProductMeasurement;
  storeCode?: string;
  image?: ProductImage;
  storageInformation?: string;
  expiryDays?: number;
  quantity?: number;
  isRefunding?: boolean;
  isRefunded?: boolean;
  isPriceLoading?: boolean;
  customAttributesForShopifyCheckoutFE?: Record<string, string>[];
  slabPrices?: SlabPrices;
  upchargePercentage?: number;
  upcharge?: Upcharge;
  upchargeMinimumThreshold?: number;
  isBulky?: boolean;
  is1PX?: boolean;
  isWeightedFresh?: boolean;
  isExpressEligible?: boolean;
  isUpchargeApplied?: boolean;
  moq?: number;
  makroId?: string;
  requestId?: string;
  installmentType?: string;
  weightedFreshPricePerUnit?: number;
  isPreOrder?: boolean;
  isAlcohol?: boolean;
  metafields?: MetafieldContract[];
  unavailableReason?: UnavailableReason;
  remarks?: string;
  recommendationScore?: number;
  alreadyPurchased?: boolean;
  isUltraFresh?: boolean;
  sellInPiece?: boolean;
  rmsStatus?: string;
  productCappedLimit?: number;
  collections?: Collection[];
  promotedPriceLineTotal?: number;
  displayPriceLineTotal?: number;
  originPriceLineTotal?: number;
  isSelected?: string;
  isLegalRestricted?: boolean;
  isRestrictedPromotion?: boolean;
};

export enum ProductType {
  PRODUCT_1P = '1p',
  PRODUCT_3P = '3p'
}

export type ProductSearchResult = {
  id: string;
  brand: string;
  title: string;
  size?: string;
  displayPrice: number;
  originPrice: number;
  priceUnit: string;
  sku: string;
  skuCode?: string;
  imageUrls: string[];
  categoryIds: string[];
  totalInventory: number;
  seller?: string;
  boostedScore?: number;
  sellerDetails?: Shop;
  variantId: string;
  storeCode: string;
  status: string;
  shortDescription?: string;
  collections?: Collection[];
  isPriceLoading?: boolean;
  variants: any[];
  slabPrices?: SlabPrices;
  shippingInfo?: ShippingInfo[];
  upcharge?: Upcharge;
  isBulky?: boolean;
  is1PX?: boolean;
  deliveryIn24Hrs?: boolean;
  isWeightedFresh?: boolean;
  makroId?: string;
  requestId?: string;
  measurements?: ProductMeasurement;
  moq?: number;
  installmentType?: string;
  isPreOrder?: boolean;
  isAlcohol?: boolean;
  isUltraFresh?: boolean;
  sellInPiece?: boolean;
  deliveryDescription?: DeliveryDescription;
  unavailableReason?: UnavailableReason;
  quantity?: number;
};

export type ComplexPromotions3P = {
  amountOff: any;
  amountPurchased: number;
  endDate: any;
  id: number;
  maxQuantityToDiscount: any;
  percentageOff: number;
  quantityPurchased: any;
  startDate: string;
  title: string;
  triggerType: string;
  type: string;
};
